<template>
    <div class="mc-follows-page">
        <div class="mc-section-heading">
            <div>
                <h1><span class="fas fa-bookmark fa-fw" aria-hidden="true"></span> Mes suivis</h1>
                <p class="mc-follows-sub">Les œuvres que vous suivez, leurs nouveautés et ce qu’il vous reste à lire.</p>
            </div>
            <span v-if="followed.length" class="mc-follows-count">{{ followed.length }} œuvre{{ followed.length > 1 ? 's' : '' }}</span>
        </div>

        <div v-if="followed.length" class="mc-follows-filters" role="group" aria-label="Filtrer les suivis">
            <button type="button" :class="{ 'is-active': filter === 'all' }" @click="filter = 'all'">Toutes</button>
            <button type="button" :class="{ 'is-active': filter === 'unread' }" @click="filter = 'unread'">
                Non lus <span v-if="totalUnread" class="mc-follows-badge">{{ totalUnread }}</span>
            </button>
        </div>

        <template v-if="visibleFollowed.length">
            <article v-for="comic in visibleFollowed" :key="comic.url" class="mc-follow-row" :class="{ 'has-new': hasNewChapter(comic) }">
                <router-link :to="comic.url" class="mc-follow-cover" tabindex="-1" aria-hidden="true"><img :src="comic.thumbnail" :alt="comic.title" loading="lazy" decoding="async"></router-link>
                <div class="mc-follow-info">
                    <h2>
                        <router-link :to="comic.url">{{ comic.title }}</router-link>
                        <span v-if="hasNewChapter(comic)" class="mc-series-new"><span class="fas fa-bell fa-fw" aria-hidden="true"></span> Nouveau</span>
                    </h2>
                    <p v-if="comic.last_chapter" class="mc-follow-last">Dernier chapitre : <router-link :to="comic.last_chapter.url" class="mc-series-chapter" :title="comic.last_chapter.full_title">{{ comic.last_chapter.full_title }}</router-link> <small v-if="comic.last_chapter.time">· {{ comic.last_chapter.time }}</small></p>
                    <p v-if="unread(comic.slug)" class="mc-follow-unread"><span class="fas fa-circle fa-fw" aria-hidden="true"></span> {{ unread(comic.slug) }} chapitre{{ unread(comic.slug) > 1 ? 's' : '' }} restant{{ unread(comic.slug) > 1 ? 's' : '' }} à lire</p>
                    <p v-else-if="unreadLoaded" class="mc-follow-uptodate"><span class="fas fa-check fa-fw" aria-hidden="true"></span> À jour</p>
                    <p v-if="comic.author" class="mc-follow-author">{{ comic.author }}</p>
                </div>
                <div class="mc-follow-actions">
                    <router-link v-if="nextChapter(comic.slug)" :to="nextChapter(comic.slug).url" class="mc-btn-primary"><span class="fas fa-book-open fa-fw" aria-hidden="true"></span> Lire la suite · {{ nextChapter(comic.slug).label }}</router-link>
                    <button v-else-if="comic.last_chapter && unreadLoaded" type="button" class="mc-btn-primary is-done" disabled title="Tous les chapitres disponibles ont été lus"><span class="fas fa-check fa-fw" aria-hidden="true"></span> À jour</button>
                    <router-link v-else-if="comic.last_chapter" :to="comic.last_chapter.url" class="mc-btn-primary"><span class="fas fa-book-open fa-fw" aria-hidden="true"></span> Lire</router-link>
                    <button v-if="unread(comic.slug)" type="button" class="mc-btn-ghost" @click="markRead(comic.slug)">Marquer comme lu</button>
                    <div class="mc-follow-secondary">
                        <button type="button" class="mc-btn-ghost" @click="markUnread(comic.slug)" title="Corriger : repasser cette œuvre comme non lue">Non lu</button>
                        <button type="button" class="mc-btn-ghost" @click="unfollow(comic.slug)">Ne plus suivre</button>
                    </div>
                </div>
            </article>
        </template>

        <div v-else-if="followed.length" class="mc-follows-empty mc-follows-empty-small">
            <h2>Tout est à jour</h2>
            <p>Vous n’avez aucun chapitre en attente dans vos suivis.</p>
        </div>

        <div v-else class="mc-follows-empty">
            <span class="fas fa-bookmark fa-2x" aria-hidden="true"></span>
            <h2>Vous ne suivez encore aucun manga</h2>
            <p>Ouvrez une fiche manga et cliquez sur « Suivre » pour la retrouver ici et voir ses nouveautés en avant.</p>
            <router-link to="/comics" class="mc-btn-primary">Parcourir le catalogue</router-link>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { hasNewChapter } from '../follows';

export default {
    name: 'Follows',
    mounted() {
        $('body').removeClass('body-reader hide-header');
        const title = 'Mes suivis | ' + this.reader.SITE_NAME;
        $('title').html(title);
        $('meta[property="og:title"]').html(title);
        this.$store.dispatch('fetchInfo', '/info');
        if (!this.comics.length) this.$store.dispatch('fetchComics', '/comics');
        this.loadUnread();
    },
    data() {
        return {
            reader: this.$root,
            filter: 'all',
            unreadMap: {},
            unreadLoaded: false,
        };
    },
    methods: {
        hasNewChapter,
        api(path) {
            return (this.reader.API_BASE_URL || '/api') + path;
        },
        csrf() {
            const meta = document.querySelector('meta[name="csrf-token"]');
            return meta ? meta.getAttribute('content') : '';
        },
        loadUnread() {
            fetch(this.api('/follows/unread'), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
                .then((response) => (response.ok ? response.json() : null))
                .then((data) => {
                    if (!data) return;
                    const map = {};
                    (data.comics || []).forEach((row) => { map[row.slug] = row; });
                    this.unreadMap = map;
                    this.unreadLoaded = true;
                })
                .catch(() => {});
        },
        unread(slug) { return this.unreadMap[slug] ? this.unreadMap[slug].unread : 0; },
        nextChapter(slug) { return this.unreadMap[slug] ? this.unreadMap[slug].next : null; },
        markRead(slug) {
            fetch(this.api('/follows/' + slug + '/read'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': this.csrf() } })
                .then(() => this.loadUnread()).catch(() => {});
        },
        markUnread(slug) {
            fetch(this.api('/follows/' + slug + '/unread'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': this.csrf() } })
                .then(() => this.loadUnread()).catch(() => {});
        },
        unfollow(slug) {
            this.$store.dispatch('toggleFollow', slug);
            delete this.unreadMap[slug];
            this.unreadMap = { ...this.unreadMap };
        },
    },
    computed: {
        ...mapGetters(['comics']),
        followed() {
            const list = this.$store.state.follows;
            return this.comics
                .filter(comic => list.includes(comic.slug))
                .slice()
                .sort((a, b) => (Date.parse(b.last_chapter && b.last_chapter.published_on) || 0) - (Date.parse(a.last_chapter && a.last_chapter.published_on) || 0));
        },
        visibleFollowed() {
            if (this.filter !== 'unread') return this.followed;
            return this.followed.filter((comic) => this.unread(comic.slug) > 0);
        },
        totalUnread() {
            return this.followed.reduce((total, comic) => total + this.unread(comic.slug), 0);
        },
    },
};
</script>
